import { memo } from "react";
import PropTypes from "prop-types";
import CardDefault from "./components/CardDefault/component";
import CardText from "./components/CardText/component";
import CardRegular from "./components/CardRegular/component";
import CardListing from "./components/CardListing/component";
import CardShow from "./components/CardShow/component";
import CardFeatured from "./components/CardFeatured/component";
import CardContentPackage from "./components/CardContentPackage/component";
import CardDiscovery from "./components/CardDiscovery/component";
import CardIconDescription from "./components/CardIconDescription/component";
import CardFeaturedReversed from "./components/CardFeatured/component.reversed";
import Image from "../ImageWrapper/component";
import PersistentPlayerPlaceholder from "../PersistentPlayer/components/Placeholder/component";
import {
    createCardIDs,
    replaceWithSquareDerivative,
} from "../../utils/dataTransform/utils";
import log from "../../utils/logger/library";
import { ITEM_TYPES, CARD_TYPES } from "./constants";

const isEqual = (prevProps, nextProps) => {
    // return prevProps.mediaItem.id === nextProps.mediaItem.id;
    if (nextProps.sourceId && nextProps.sourceId !== prevProps.sourceId) {
        return false;
    }
    if (
        nextProps.type !== prevProps.type ||
        nextProps.updateTime !== prevProps.updateTime
    ) {
        return false;
    }
    return true;
};

const Card = memo((props) => {
    const {
        type = CARD_TYPES.TYPE_DEFAULT,
        idPrefix = "card",
        category = "",
        description = "",
        contentId = 0,
        itemType = ITEM_TYPES.ITEM_TYPE_STORY,
        showTimestamp = true,
        sourceId = "",
        title = "",
        url = "",
        cardIndex = 0,
        videoId = null,
        imageURL,
        source,
        videoAirDate,
        videoDuration,
        videoHasCaptions,
        videoStreamType,
        videoTitle,
    } = props;
    // Get the <Image /> displayed by the card (if any).
    const image = getImage({ imageURL, source });

    // To get the Video displayed by the card (if any)
    const media =
        hasVideo({ videoId, cardIndex, type }) &&
        getVideo({
            type,
            videoId,
            source,
            videoAirDate,
            videoDuration,
            category,
            videoHasCaptions,
            videoStreamType,
            imageURL,
            url,
            videoTitle,
            title,
        });

    //card id's
    const cardIDs = createCardIDs(idPrefix, sourceId);

    const finalProps = Object.assign(
        {},
        props,
        {
            type,
            idPrefix,
            category,
            description,
            contentId,
            itemType,
            showTimestamp,
            sourceId,
            title,
            url,
            cardIndex,
            videoId,
        },
        cardIDs,
        { image: image },
        { media: media }
    );

    // Render a sub-card type based on the prop values.
    const card = getCard(finalProps);
    return card;
}, isEqual);

/**
 * Check to see if the card has a video
 *
 * @param {Object} props The component props
 * @returns {true | null} returns true if it has video, else returns null
 */
export function hasVideo({ videoId, cardIndex, type }) {
    if (
        videoId &&
        (cardIndex === 0 || type === CARD_TYPES.TYPE_CONTENT_PACKAGE)
    ) {
        return true;
    } else {
        return null;
    }
}

/**
 * Get video title
 *
 * @param {Object} props The component props
 * @returns {String} returns video title
 */
export function getVideoTitle(props) {
    if (props.videoTitle) {
        return props.videoTitle;
    }
    return props.title;
}

/**
 * Construct the <PersistentPlayerPlaceholder /> that will be displayed by the card (if any).
 *
 * A video is returned in the props
 *
 * @param {Object} props The component props
 * @returns {PersistentPlayerPlaceholder|null} A <PersistentPlayerPlaceholder /> component, or null
 */
export function getVideo({
    type,
    videoId,
    source,
    videoAirDate,
    videoDuration,
    category,
    videoHasCaptions,
    videoStreamType,
    imageURL,
    url,
    videoTitle,
    title,
}) {
    try {
        let featureAttribution = {};
        if (type === "contentpackage") {
            featureAttribution.name = "content-package";
            featureAttribution.instance = "primary-visual";
        }

        const persistentPlayerProps = {
            mediaItem: {
                sourceId: videoId,
                mediaId: videoId,
                source: source,
                title: getVideoTitle({ videoTitle, title }),
                publishedAt: videoAirDate,
                type: "video",
                image: {
                    url: imageURL ?? null,
                },
                media: {
                    duration: videoDuration,
                    contentArea: category,
                    hasCaptions: videoHasCaptions,
                    streamType: videoStreamType,
                },
            },
            config: {
                autoPlay: false,
            },
            featureAttribution: featureAttribution,
            disableRecommendations: true,
        };

        return (
            <div className="videoContainer">
                <PersistentPlayerPlaceholder {...persistentPlayerProps} />
            </div>
        );
    } catch (error) {
        log.error(`(getVideo) Failed to embed Video in Card ${url}`, error);
    }

    return null;
}

/**
 * Construct the <Image /> that will be displayed by the card (if any).
 *
 * An image is returned in the props
 *
 * @param {Object} props The component props
 * @returns {Image|null} An <Image />, or null
 */
function getImage({ imageURL: imageURLProp, source }) {
    const imageURL = imageURLProp
        ? replaceWithSquareDerivative(imageURLProp)
        : "";

    const imageProps = {
        url: imageURL,
    };
    const image =
        source === "Polopoly" && imageURLProp ? (
            <Image {...imageProps} />
        ) : null;
    return image;
}

/**
 * Get the card type and return it.
 * @param {object} props Card Props
 * @returns {jsx} Return Card JSX
 */
function getCard(props) {
    let card = null;

    switch (props.type) {
        case CARD_TYPES.TYPE_REGULAR:
            card = <CardRegular {...props} />;
            break;

        case CARD_TYPES.TYPE_LISTING:
            card = <CardListing {...props} />;
            break;

        case CARD_TYPES.TYPE_LISTING_HORIZONTAL:
            card = <CardListing imagePosition={"right"} {...props} />;
            break;
        case CARD_TYPES.TYPE_TEXT:
            card = <CardText {...props} />;
            break;
        case CARD_TYPES.TYPE_SHOW:
            card = <CardShow {...props} />;
            break;
        case CARD_TYPES.TYPE_FEATURED:
            card = <CardFeatured {...props} />;
            break;
        case CARD_TYPES.TYPE_FEATURED_AUTHOR:
            card = <CardFeatured displayAuthor={true} {...props} />;
            break;
        case CARD_TYPES.TYPE_CONTENT_PACKAGE:
            card = <CardContentPackage {...props} />;
            break;
        case CARD_TYPES.TYPE_DISCOVERY:
            card = <CardDiscovery {...props} />;
            break;
        case CARD_TYPES.TYPE_ICON_DESCRIPTION:
            card = <CardIconDescription {...props} />;
            break;
        case CARD_TYPES.TYPE_FEATURED_REVERSED:
            card = <CardFeaturedReversed {...props} />;
            break;
        case CARD_TYPES.TYPE_LISTING_AUTHOR:
            card = <CardListing displayAuthor={true} {...props} />;
            break;
        default:
            card = <CardDefault {...props} />;
    }

    return card;
}

Card.propTypes = {
    type: PropTypes.oneOf([
        CARD_TYPES.TYPE_DEFAULT,
        CARD_TYPES.TYPE_REGULAR,
        CARD_TYPES.TYPE_TEXT,
        CARD_TYPES.TYPE_LISTING,
        CARD_TYPES.TYPE_LISTING_HORIZONTAL,
        CARD_TYPES.TYPE_SHOW,
        CARD_TYPES.TYPE_FEATURED,
        CARD_TYPES.TYPE_FEATURED_AUTHOR,
        CARD_TYPES.TYPE_CONTENT_PACKAGE,
        CARD_TYPES.TYPE_ICON_DESCRIPTION,
        CARD_TYPES.TYPE_DISCOVERY,
        CARD_TYPES.TYPE_LISTING_AUTHOR,
        CARD_TYPES.TYPE_FEATURED_REVERSED,
    ]),
    // E.g. World, Business, Health, Science, etc.
    category: PropTypes.string,
    description: PropTypes.string,
    flag: PropTypes.string,
    contentId: PropTypes.number,
    imageURL: PropTypes.string,
    itemType: PropTypes.oneOf(ITEM_TYPES),
    source: PropTypes.oneOf(["Polopoly", "9"]),
    sourceId: PropTypes.string,
    title: PropTypes.string,
    showTimestamp: PropTypes.bool,
    updateTime: PropTypes.number,
    url: PropTypes.string,
    idPrefix: PropTypes.string,
    className: PropTypes.string,
    contentPackage: PropTypes.object,
    videoId: PropTypes.string,
};

export default Card;
